import type { SortOption } from "./sort-radios.shared";
import type { ViewToggleOption } from "./view-toggle.shared";

/**
 * The lineup blok's three control fields, turned from what Storyblok publishes into what the DDS
 * preparers take. Two callers, one copy.
 */

/**
 * `commonSbFixer` collapses A one-element array to the element (`lib/helper/sb-fixer.ts:14-21`) and
 * drops falsy values, so a one-option toggle arrives as an object and an empty list arrives as
 * `null`.
 */
function authoredList(raw: unknown): unknown[] {
  if (Array.isArray(raw)) {
    return raw;
  }
  return raw === null || raw === undefined ? [] : [raw];
}

function trimmed(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * VT1/VT2 — the view toggle's authored options, in authored order. `value` is the view ID, not free
 * text: `resolveLineupForBlok` maps it through `signature` | `genre` and `LineupSelectedView`
 * switches on the same two.
 */
export function viewToggleOptionsOf(raw: unknown): ViewToggleOption[] {
  return authoredList(raw)
    .filter(entry => !!entry && typeof entry === "object")
    .map(entry => ({
      value: trimmed((entry as Record<string, unknown>).value).toLowerCase(),
      label: trimmed((entry as Record<string, unknown>).label)
    }));
}

/**
 * GH4 — the sort control's authored options, in authored order. Nothing authored leaves
 * `prepareSortOptions` to fall back to A-Z / Category. Do not lowercase `value` here the way
 * `viewToggleOptionsOf` does.
 */
export function sortOptionsOf(raw: unknown): SortOption[] {
  return authoredList(raw)
    .filter(entry => !!entry && typeof entry === "object")
    .map(entry => ({
      value: trimmed((entry as Record<string, unknown>).value),
      label: trimmed((entry as Record<string, unknown>).label)
    }));
}

/**
 * VT2 steps 2-4 — the content-managed "genre trigger" pages, as the referrer substrings legacy
 * matches on (`channel-lineup-page-adapter.ts:587` reads the same list from
 * `chlupDefaultTab.genrePackTriggers`).
 */
export function genreTriggerPagesOf(raw: unknown): string[] {
  const pages: string[] = [];
  for (const entry of authoredList(raw)) {
    const page = trimmed(entry);
    if (page !== "") {
      pages.push(page);
    }
  }
  return pages;
}

/**
 * VT2 step 2's `arrivedFromTriggerPage`, from the `referer` header the page already reads
 * (`app/v3/[...slug]/page.tsx:103` → `personalizationTriggers.previousURL`).
 */
export function isTriggerReferrer(previousURL: unknown, triggerPages: string[]): boolean {
  const referrer = trimmed(previousURL);
  if (referrer === "") {
    return false;
  }
  return triggerPages.some(page => referrer.includes(page));
}
